import React, { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { ClaimCard } from '../components/ClaimCard';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Trophy, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { formatNumber } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClaimPage = () => {
  const { address, isConnected } = useAccount();
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState(null);
  const [epochs, setEpochs] = useState([]);
  
  useEffect(() => {
    if (isConnected && address) {
      loadRewards();
    }
  }, [address, isConnected]);
  
  const loadRewards = async () => {
    try {
      setLoading(true);
      
      // Get wallet rewards
      const rewardsRes = await axios.get(`${BACKEND_URL}/api/rewards/${address}`);
      setRewards(rewardsRes.data);
      
      // Get epoch details for all epochs user participated in
      const epochIds = [...new Set(rewardsRes.data.epochs.map(e => e.epochId))];
      const epochPromises = epochIds.map(id => 
        axios.get(`${BACKEND_URL}/api/epochs/${id}`).catch(err => null)
      );
      const epochResponses = await Promise.all(epochPromises);
      const validEpochs = epochResponses.filter(r => r !== null).map(r => r.data);
      setEpochs(validEpochs);
      
    } catch (error) {
      console.error('Error loading rewards:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isConnected) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-2xl mx-auto p-12 text-center border-[#b8b3ab] bg-[#e8e3db]/50" data-testid="connect-wallet-prompt">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-[#f9a825]" />
          <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
          <p className="text-[#666666]">
            Connect your wallet to view and claim your TRUE Points rewards
          </p>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#f9a825]" />
      </div>
    );
  }
  
  const unclaimedRewards = rewards?.epochs?.filter(e => !e.claimed) || [];
  const claimedRewards = rewards?.epochs?.filter(e => e.claimed) || [];
  
  return (
    <div className="container mx-auto px-4 py-12" data-testid="claim-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-2">Your Rewards</h1>
        <p className="text-[#666666] mb-8">Claim your TRUE Points rewards from completed epochs</p>
        
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="p-6 border-[#b8b3ab] bg-[#e8e3db]/50">
            <p className="text-sm text-[#666666] mb-1">Total Earned</p>
            <p className="text-2xl font-bold text-[#f9a825]">{formatNumber(rewards?.totalEarned || 0)} TIP</p>
          </Card>
          <Card className="p-6 border-[#b8b3ab] bg-[#e8e3db]/50">
            <p className="text-sm text-[#666666] mb-1">Claimed</p>
            <p className="text-2xl font-bold text-[#4caf50]">{formatNumber(rewards?.totalClaimed || 0)} TIP</p>
          </Card>
          <Card className="p-6 border-[#b8b3ab] bg-[#e8e3db]/50">
            <p className="text-sm text-[#666666] mb-1">Unclaimed</p>
            <p className="text-2xl font-bold text-[#f9a825]">{formatNumber(rewards?.totalUnclaimed || 0)} TIP</p>
          </Card>
        </div>
        
        {/* Gas Warning */}
        <Alert className="mb-8 border-amber-500/20 bg-amber-500/10">
          <AlertCircle className="h-4 w-4 text-[#f9a825]" />
          <AlertDescription className="text-[#f9a825]">
            You'll need ~0.01-0.03 BNB for gas fees to claim rewards. Make sure your wallet has enough BNB.
          </AlertDescription>
        </Alert>
        
        {/* Unclaimed Rewards */}
        {unclaimedRewards.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Available to Claim</h2>
            <div className="grid gap-4">
              {unclaimedRewards.map(reward => {
                const epoch = epochs.find(e => e.epochId === reward.epochId);
                return epoch ? (
                  <ClaimCard 
                    key={reward.epochId} 
                    epoch={epoch} 
                    reward={reward}
                    wallet={address}
                    onClaimSuccess={loadRewards}
                  />
                ) : null;
              })}
            </div>
          </div>
        )}
        
        {/* Claimed Rewards */}
        {claimedRewards.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Claim History</h2>
            <div className="grid gap-4">
              {claimedRewards.map(reward => {
                const epoch = epochs.find(e => e.epochId === reward.epochId);
                return epoch ? (
                  <ClaimCard 
                    key={reward.epochId} 
                    epoch={epoch} 
                    reward={reward}
                    wallet={address}
                    onClaimSuccess={loadRewards}
                  />
                ) : null;
              })}
            </div>
          </div>
        )}
        
        {unclaimedRewards.length === 0 && claimedRewards.length === 0 && (
          <Card className="p-12 text-center border-[#b8b3ab] bg-[#e8e3db]/50">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-xl font-bold mb-2">No Rewards Yet</h3>
            <p className="text-[#666666]">
              Start tipping to earn TRUE Points rewards!
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};
